import { PlusIcon } from 'lucide-react';
import { makeAutoObservable, runInAction } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useEffect } from 'react';
import Box from '../../uikit/Box';
import Frame from '../../uikit/Frame';
import { useMobxInstance } from '../../utils/mobxUtil';
import { useCommonNavigate } from '../common/CommonLink';
import Jsonld from '../Jsonld';
import MetaHeader from '../MetaHeader';
import ChallengeRankingList from './ChallengeRankingList';
import PeriodFilterTabs, { PeriodType } from './PeriodFilterTabs';

// 임시 목 데이터 인터페이스
interface RankingStrategy {
    hash: string;
    rank: number;
    name: string;
    userName?: string;
    returnRate: number;
    topHoldings: Array<{
        ticker: string;
        weight: number;
    }>;
}

class ViewStore {
    selectedPeriod: PeriodType = '1M';
    strategies: RankingStrategy[] = [];
    isLoading = false;

    constructor() {
        makeAutoObservable(this);
    }

    async fetch() {
        this.isLoading = true;

        // TODO: API 연동 (추후 구현)
        // const result = await apiClient.challenge.getRanking.$query({
        //     period: this.selectedPeriod
        // });

        // 임시 목 데이터
        await new Promise((resolve) => setTimeout(resolve, 500));

        runInAction(() => {
            this.strategies = this.getMockData();
            this.isLoading = false;
        });
    }

    setPeriod(period: PeriodType) {
        this.selectedPeriod = period;
        this.fetch();
    }

    // 임시 목 데이터 생성 함수
    private getMockData(): RankingStrategy[] {
        return [
            {
                hash: 'abc123',
                rank: 1,
                name: '빅테크 성장 전략',
                userName: '투자왕',
                returnRate: 0.1523,
                topHoldings: [
                    { ticker: 'AAPL', weight: 0.3 },
                    { ticker: 'MSFT', weight: 0.25 },
                    { ticker: 'GOOGL', weight: 0.2 },
                ],
            },
            {
                hash: 'def456',
                rank: 2,
                name: '안정 배당 포트폴리오',
                userName: '배당러버',
                returnRate: 0.0845,
                topHoldings: [
                    { ticker: 'JNJ', weight: 0.25 },
                    { ticker: 'PG', weight: 0.25 },
                    { ticker: 'KO', weight: 0.2 },
                ],
            },
            {
                hash: 'ghi789',
                rank: 3,
                name: 'S&P500 따라잡기',
                returnRate: 0.0723,
                topHoldings: [
                    { ticker: 'SPY', weight: 0.5 },
                    { ticker: 'VOO', weight: 0.3 },
                ],
            },
            {
                hash: 'jkl012',
                rank: 4,
                name: '반도체 집중 투자',
                userName: '칩매니아',
                returnRate: 0.0615,
                topHoldings: [
                    { ticker: 'NVDA', weight: 0.35 },
                    { ticker: 'AMD', weight: 0.25 },
                    { ticker: 'TSM', weight: 0.2 },
                ],
            },
            {
                hash: 'mno345',
                rank: 5,
                name: '헬스케어 미래',
                returnRate: 0.0489,
                topHoldings: [
                    { ticker: 'UNH', weight: 0.3 },
                    { ticker: 'JNJ', weight: 0.25 },
                ],
            },
        ];
    }
}

const ChallengePage = observer(() => {
    const viewStore = useMobxInstance(ViewStore);
    const { navigate } = useCommonNavigate();

    useEffect(() => {
        viewStore.fetch();
    }, [viewStore]);

    const title = '챌린지 랭킹';
    const description = '다양한 투자 전략의 성과를 비교하고 나만의 전략으로 도전해보세요.';

    return (
        <Frame>
            <MetaHeader title={title} description={description} />
            <Jsonld type="Blog" title={title} description={description} />
            <Frame.Title title={title} />

            {/* 기간 필터 */}
            <Box>
                <PeriodFilterTabs
                    selectedPeriod={viewStore.selectedPeriod}
                    onPeriodChange={(period) => viewStore.setPeriod(period)}
                />
            </Box>

            {/* 랭킹 리스트 */}
            <Box>
                <ChallengeRankingList
                    strategies={viewStore.strategies}
                    isLoading={viewStore.isLoading}
                />
            </Box>

            {/* 하단 CTA 영역 */}
            {!viewStore.isLoading && viewStore.strategies.length > 0 && (
                <Box>
                    <div className="flex flex-col gap-4 text-center">
                        <div>
                            <div className="text-base-content mb-1 text-base font-bold">
                                나만의 생각을 시험해보세요
                            </div>
                            <div className="text-base-content/60 text-sm">
                                매일 수익률이 자동으로 기록됩니다.
                            </div>
                        </div>

                        <button
                            className="btn btn-primary gap-2"
                            onClick={(e) => {
                                e.preventDefault();
                                navigate('/strategy/create');
                            }}
                        >
                            <PlusIcon className="size-5" />내 전략 만들기
                        </button>

                        <div className="text-base-content/50 text-xs">
                            1분이면 충분해요. AI가 도와드려요.
                        </div>
                    </div>
                </Box>
            )}

            <Frame.BottomSpacer />
        </Frame>
    );
});

export default ChallengePage;
